import {
  CHAIN_FLOW_OPTIONS,
  FLOW_TYPES,
  REQUEST_TYPES,
} from './constants';

export const CHAIN_API_TYPES = {
  HIRE: 'HIRE_APPROVAL',
  CAMPAIGN: 'CAMPAIGN_APPROVAL',
};

export const CHAIN_API_FLOW = {
  SEQUENTIAL: 'SEQUENTIAL',
  PARALLEL: 'PARALLEL',
};

export const mapUiTypeToApi = (type) => {
  if (type === REQUEST_TYPES.CAMPAIGN) return CHAIN_API_TYPES.CAMPAIGN;
  return CHAIN_API_TYPES.HIRE;
};

export const mapApiTypeToUi = (type) => {
  if (type === CHAIN_API_TYPES.CAMPAIGN) return REQUEST_TYPES.CAMPAIGN;
  return REQUEST_TYPES.HIRE;
};

export const mapUiFlowToApi = (flow) => {
  if (flow === FLOW_TYPES.PARALLEL) return CHAIN_API_FLOW.PARALLEL;
  return CHAIN_API_FLOW.SEQUENTIAL;
};

export const mapApiFlowToUi = (flow) => {
  if (flow === CHAIN_API_FLOW.PARALLEL) return FLOW_TYPES.PARALLEL;
  return FLOW_TYPES.SEQUENTIAL;
};

export const getAccountDisplayName = (account = {}) => {
  const fromParts = [account.firstName, account.lastName].filter(Boolean).join(' ').trim();
  if (fromParts) return fromParts;

  const fallback =
    account.name ||
    account.fullName ||
    account.displayName ||
    account.label ||
    account.title ||
    account.email;

  return typeof fallback === 'string' ? fallback.trim() : '';
};

export const getAccountCompanyRoleNames = (account = {}) => {
  const fromCompany = (account.accountCompanyRoles || [])
    .map((item) => item?.companyRole?.name || item?.companyRole?.title)
    .filter(Boolean);

  if (fromCompany.length) return [...new Set(fromCompany)];

  const fromLegacy = (account.accountRoles || [])
    .map((item) => item?.role?.title || item?.role?.name || item?.title || item?.name)
    .filter(Boolean);

  return [...new Set(fromLegacy)];
};

export const getAccountRoleLabel = (account = {}) => account.jobTitle?.trim() || '';

export const formatApproverRolesLabel = (approver = {}) =>
  approver.jobTitle?.trim() || getAccountRoleLabel(approver);

export const getCompanyRoleTagStyle = (roleName = '', theme = {}) => {
  const key = roleName.toLowerCase().trim();
  const styles = {
    approver: {
      backgroundColor: theme?.dark ? theme.tableHeaderBg : theme.smokegray,
      color: theme?.dark ? theme.statusTextColor : theme.textColor,
    },
    interviewer: {
      backgroundColor: theme?.dark ? `${theme.gold}33` : theme.tagLightYellow,
      color: theme?.dark ? theme.headingColor : theme.gold,
    },
    'billing admin': {
      backgroundColor: theme?.dark ? theme.tagBlueBgColor || theme.primaryLight4 : theme.primaryLight4,
      color: theme?.dark ? theme.tagBlueColor || theme.blue : theme.blue,
    },
    billing: {
      backgroundColor: theme?.dark ? theme.tagBlueBgColor || theme.primaryLight4 : theme.primaryLight4,
      color: theme?.dark ? theme.tagBlueColor || theme.blue : theme.blue,
    },
    'hiring manager': {
      backgroundColor: theme?.darkTagGreenBg || theme.interviewAttend,
      color: theme?.darkTagGreenTextColor || theme.blue,
    },
    admin: {
      backgroundColor: theme?.dark ? theme.statusTagNotSent : theme.statusTagNotSent,
      color: theme?.dark ? theme.statusTextColor : theme.textColor,
    },
    recruiter: {
      backgroundColor: theme?.dark ? theme.statusTagNew : theme.secondaryLight2,
      color: theme?.dark ? theme.tagBlueBgColor : theme.secondary,
    },
  };

  return (
    styles[key] || {
      backgroundColor: theme?.dark ? theme.tableHeaderBg : theme.smokegray,
      color: theme?.dark ? theme.statusTextColor : theme.textColor,
    }
  );
};

export const mapChainApproverFromApi = (approver) => {
  const account = approver?.account || approver;
  const accountId = account?.id || approver?.accountId;
  const name = getAccountDisplayName(account);
  const companyRoles = getAccountCompanyRoleNames(account);
  const jobTitle = getAccountRoleLabel(account);

  return {
    value: accountId,
    userId: accountId,
    name,
    role: jobTitle,
    jobTitle: account.jobTitle,
    label: name,
    companyRoles,
    accountCompanyRoles: account?.accountCompanyRoles,
    signedUrl: account?.signedUrl,
    email: account?.email,
    order: approver?.order,
  };
};

export const mapEligibleApproverFromApi = (account) => {
  const name = getAccountDisplayName(account);
  const companyRoles = getAccountCompanyRoleNames(account);
  const jobTitle = getAccountRoleLabel(account);

  return {
    ...account,
    id: account.id,
    value: account.id,
    firstName: account.firstName,
    lastName: account.lastName,
    label: name,
    name,
    role: jobTitle,
    jobTitle: account.jobTitle,
    companyRoles,
  };
};

export const mapApprovalChainFromApi = (chain) => {
  const flowType = mapApiFlowToUi(chain?.flow);
  const approvers = (chain?.approvers || []).map(mapChainApproverFromApi);

  return {
    id: chain.id,
    title: chain.name,
    name: chain.name,
    description: chain.description || '',
    type: mapApiTypeToUi(chain.type),
    flowType,
    flow: chain.flow,
    approvers,
    rolesSummary: approvers
      .map((a) => a.role)
      .filter(Boolean)
      .join(flowType === FLOW_TYPES.PARALLEL ? ' + ' : ' → '),
    createdAt: chain.createdAt,
    updatedAt: chain.updatedAt,
  };
};

export const mapApprovalChainListParams = (params = {}) => {
  const { type, flow, ...rest } = params;
  const mapped = { ...rest };

  if (type && type !== 'all') {
    mapped.type = mapUiTypeToApi(type);
  }

  if (flow) {
    mapped.flow = mapUiFlowToApi(flow);
  }

  return mapped;
};

export const mapApprovalChainToFormValues = (chain) => {
  const flowType = chain.flowType || mapApiFlowToUi(chain.flow);
  const flowOption = CHAIN_FLOW_OPTIONS.find((o) => o.value === flowType);

  return {
    id: chain.id ?? null,
    name: chain.name || chain.title || '',
    type: chain.type || REQUEST_TYPES.HIRE,
    description: chain.description || '',
    flowType: flowOption?.label || CHAIN_FLOW_OPTIONS[0].label,
    approvers: (chain.approvers || []).map((a, index) => ({
      value: a.value ?? a.userId ?? a.accountId ?? `chain-approver-${chain.id}-${index}`,
      label: a.label || (a.role ? `${a.name} — ${a.role}` : a.name),
      name: a.name,
      role: a.role,
      email: a.email,
    })),
    _approverPicker: null,
  };
};

export const buildApprovalChainApiBody = (values, { getApprovalFlowType }) => {
  const flow = mapUiFlowToApi(getApprovalFlowType(values.flowType));

  return {
    name: values.name?.trim(),
    description: values.description?.trim() ?? '',
    type: mapUiTypeToApi(values.type),
    flow,
    approvers: (values.approvers || [])
      .map((a) => a.value ?? a.userId ?? a.id)
      .filter(Boolean),
  };
};
